import React, { FC, useEffect, useRef } from 'react';
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Select,
} from '@duxton-web/ui';

import {
  getAppOptionsMap,
  osOptionsMap,
  parseAppVersion,
} from '@/common/AppVersionInput/appVersionUtils';
import { getTenant } from '@/common/Tenants';

interface AppVersionPromptProps {
  value?: string;
  onSubmit?: (value: string, prev: string) => void;
  onCancel?: () => void;
}

const versionRegex = /^\d+\.\d+\.\d+$/;

const AppVersionField = {
  APP: 'app',
  OS: 'os',
  VERSION: 'version',
};

const AppVersionPrompt: FC<AppVersionPromptProps> = ({
  value = '',
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const [form] = Form.useForm();
  const app = Form.useWatch(AppVersionField.APP, form);
  const os = Form.useWatch(AppVersionField.OS, form);
  const version = Form.useWatch(AppVersionField.VERSION, form);
  const appOptionsMap = getAppOptionsMap(getTenant());
  const formRef = useRef(null);

  useEffect(() => {
    const [app, os, version] = parseAppVersion(value);
    form.setFieldsValue({
      app: app,
      os: os,
      version: version,
    });
  }, []);

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Flex vertical gap={8} ref={formRef}>
        <Form form={form} layout="vertical">
          <Flex gap={8}>
            <Form.Item name={AppVersionField.APP} label="App" required>
              <Select
                data-testid="app-version-prompt-app"
                options={Object.keys(appOptionsMap).map((opt) => {
                  return {
                    value: opt,
                    label: appOptionsMap[opt],
                  };
                })}
                style={{
                  width: 150,
                }}
                showSearch
                optionFilterProp="label"
                getPopupContainer={() => formRef.current}
              />
            </Form.Item>
            <Form.Item name={AppVersionField.OS} label="OS" required>
              <Select
                data-testid="app-version-prompt-os"
                options={Object.keys(osOptionsMap).map((o) => {
                  return {
                    value: o,
                    label: osOptionsMap[o],
                  };
                })}
                style={{
                  width: 150,
                }}
                showSearch
                optionFilterProp="label"
                getPopupContainer={() => formRef.current}
              />
            </Form.Item>
            <Form.Item
              name={AppVersionField.VERSION}
              label="Version"
              rules={[
                {
                  pattern: versionRegex,
                  message: 'Invalid format. (e.g., 1.0.0)',
                },
              ]}
              required
            >
              <Input
                data-testid="app-version-prompt-version"
                style={{
                  width: 150,
                }}
              />
            </Form.Item>
          </Flex>
        </Form>
        <Flex justify="flex-end" gap={8}>
          <Button
            size="small"
            onClick={() => {
              form.resetFields();

              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={
              !app || !os || version === '' || !version.match(versionRegex)
            }
            onClick={() => {
              form.resetFields();

              onSubmit(`${app}-${os}-${version}`, value);
            }}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </ConfigProvider>
  );
};

export default AppVersionPrompt;
